<script setup lang="ts">
  /**
   * Dialog 内容组件
   * @description
   * 1. 弹窗的主要内容区域
   * 2. 包含遮罩层(Overlay)和内容容器
   * 3. 处理弹窗动画和定位
   */
  import type { DialogContentEmits, DialogContentProps } from 'reka-ui'
  import type { HTMLAttributes } from 'vue'
  import { reactiveOmit } from '@vueuse/core'
  import { X } from 'lucide-vue-next'
  import {
    DialogClose,
    DialogContent,
    DialogOverlay,
    DialogPortal,
    useForwardPropsEmits,
  } from 'reka-ui'
  import { cn } from '@rapoo/element/lib/utils'

  const props = withDefaults(
    defineProps<
      DialogContentProps & {
        class?: HTMLAttributes['class']
        overlayClass?: HTMLAttributes['class']
        showClose?: boolean
        closeOnOverlayClick?: boolean
        restoreFocus?: boolean
      }
    >(),
    {
      showClose: true,
      closeOnOverlayClick: true,
      restoreFocus: true,
    },
  )
  const emits = defineEmits<DialogContentEmits>()

  const delegatedProps = reactiveOmit(
    props,
    'class',
    'overlayClass',
    'showClose',
    'closeOnOverlayClick',
    'restoreFocus',
  )

  const forwarded = useForwardPropsEmits(delegatedProps, emits)
  const handlePointerDownOutside = (e: any) => {
    if (!props.closeOnOverlayClick) {
      e.preventDefault()
    }
  }

  const handleCloseAutoFocus = (event: Event) => {
    if (!props.restoreFocus) event.preventDefault()
  }
</script>

<template>
  <DialogPortal>
    <DialogOverlay
      :class="
        cn(
          'fixed inset-0 z-dialog bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          props.overlayClass,
        )
      "
    />
    <DialogContent
      v-bind="forwarded"
      :class="
        cn(
          'fixed left-1/2 top-1/2 z-dialog grid w-full max-w-lg -translate-x-1/2 -translate-y-1/2 gap-4 border glass-m60 p-6 duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 sm:rounded-lg',
          props.class,
        )
      "
      @pointer-down-outside="handlePointerDownOutside"
      @close-auto-focus="handleCloseAutoFocus"
    >
      <slot />

      <DialogClose
        v-if="showClose"
        class="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground"
      >
        <X class="w-4 h-4" />
        <span class="sr-only">Close</span>
      </DialogClose>
    </DialogContent>
  </DialogPortal>
</template>
